import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, Card, Checkbox, Modal, Radio, Select, Spin, Table, Typography } from "antd";
import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import { API_BASE } from "./config/api";
import {
  getDistributorToken,
  handleDistributorAuthFailure,
} from "./config/auth";

const authConfig = () => ({
  headers: { "X-KG-Distributor-Authorization": `Bearer ${getDistributorToken()}` },
});

const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const qty = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 6 });
};

const nav = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const displayDate = (value) => {
  if (!value) return "-";
  const [y, m, d] = String(value).split("-");
  if (!y || !m || !d) return value;
  return `${d}-${m}-${y}`;
};

const PERIOD_TITLES = [
  "01/04 to 15/06",
  "16/06 to 15/09",
  "16/09 to 15/12",
  "16/12 to 15/03",
  "16/03 to 31/03",
  "Total",
];

function SummaryTable({ matrix }) {
  const rows = [];
  [
    ["Short Term", matrix?.short_term],
    ["Long Term", matrix?.long_term],
  ].forEach(([termLabel, term]) => {
    [
      ["Total Redemptions/Sale", "full_value_consideration"],
      ["Total Purchases", "cost_of_acquisition"],
      ["Profit / Loss", "capital_gain_loss"],
    ].forEach(([particular, key], index) => {
      rows.push({
        key: `${termLabel}-${key}`,
        term: termLabel,
        termRowSpan: index === 0 ? 3 : 0,
        particular,
        values: term?.[key] || [0, 0, 0, 0, 0, 0],
      });
    });
  });

  const columns = [
    {
      title: "Type",
      dataIndex: "term",
      width: 120,
      onCell: (record) => ({ rowSpan: record.termRowSpan }),
      render: (v) => <strong>{v}</strong>,
    },
    { title: "Particulars", dataIndex: "particular", width: 220 },
    ...PERIOD_TITLES.map((title, i) => ({
      title,
      key: title,
      align: "right",
      width: 145,
      render: (_, row) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(row.values[i])}</span>,
    })),
  ];

  return (
    <>
      <style>{`
        .cg-summary-sale-row > td { background: #eef6ff !important; }
        .cg-summary-purchase-row > td { background: #fff8e8 !important; }
        .cg-summary-profit-row > td { background: #eef9f0 !important; font-weight: 600; }
        .cg-summary-sale-row:hover > td { background: #e4f0ff !important; }
        .cg-summary-purchase-row:hover > td { background: #fff3d6 !important; }
        .cg-summary-profit-row:hover > td { background: #e3f5e7 !important; }
      `}</style>
      <Table
      bordered
      size="small"
      pagination={false}
      rowKey="key"
      columns={columns}
      dataSource={rows}
      rowClassName={(record) => {
        if (record.particular === "Total Redemptions/Sale") return "cg-summary-sale-row";
        if (record.particular === "Total Purchases") return "cg-summary-purchase-row";
        if (record.particular === "Profit / Loss") return "cg-summary-profit-row";
        return "";
      }}
      scroll={{ x: 1200 }}
    />
    </>
  );
}

function DetailsTable({ rows }) {
  // MFCentral-style useful columns only. Internal transaction type/trace fields stay hidden.
  const columns = [
    {
      title: "Subscription / Purchase",
      children: [
        { title: "Date", width: 105, render: (_, r) => displayDate(r.subscription?.date) },
        { title: "Units", width: 105, align: "right", render: (_, r) => qty(r.subscription?.matched_units) },
        { title: "NAV", width: 95, align: "right", render: (_, r) => nav(r.subscription?.nav) },
        { title: "Total Purchases", width: 140, align: "right", render: (_, r) => money(r.subscription?.cost_of_acquisition) },
      ],
    },
    {
      title: "Redemption / Sale",
      children: [
        { title: "Date", width: 105, render: (_, r) => displayDate(r.outflow?.date) },
        { title: "Units", width: 105, align: "right", render: (_, r) => qty(r.outflow?.matched_units) },
        { title: "NAV", width: 95, align: "right", render: (_, r) => nav(r.outflow?.nav) },
        { title: "Total Redemptions/Sale", width: 165, align: "right", render: (_, r) => money(r.outflow?.full_value_consideration) },
      ],
    },
    {
      title: "Profit / Loss",
      children: [
        { title: "Short Term", width: 120, align: "right", render: (_, r) => money(r.gains?.short_term_gain_loss) },
        { title: "Long Term", width: 120, align: "right", render: (_, r) => money(r.gains?.long_term_gain_loss) },
      ],
    },
  ];

  const grouped = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((r) => {
      const schemeName = r.scheme_name || r.scheme_code || "Unknown Scheme";
      const folio = r.folio_number || r.outflow?.folio_number || r.subscription?.folio_number || "-";
      const key = `${r.scheme_code || schemeName}||${folio}`;
      if (!map.has(key)) map.set(key, { key, schemeName, folio, rows: [] });
      map.get(key).rows.push(r);
    });
    return Array.from(map.values());
  }, [rows]);

  return (
    <div>
      {grouped.map((group) => (
        <div key={group.key} style={{ marginBottom: 18 }}>
          <div style={{ background: "#f5f7fa", border: "1px solid #d9d9d9", borderBottom: 0, padding: "7px 10px" }}>
            <Typography.Text strong>{group.schemeName}</Typography.Text>
            <Typography.Text type="secondary" style={{ marginLeft: 12 }}>Folio: {group.folio}</Typography.Text>
          </div>
          <Table
            bordered
            size="small"
            pagination={false}
            rowKey={(r) => r.fifo_match_id}
            columns={columns}
            dataSource={group.rows}
            scroll={{ x: 1250 }}
          />
        </div>
      ))}
    </div>
  );
}

export default function CapitalGainsReport() {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientError, setClientError] = useState("");
  const [selectedPan, setSelectedPan] = useState();
  const [years, setYears] = useState([]);
  const [yearsLoading, setYearsLoading] = useState(false);
  const [yearError, setYearError] = useState("");
  const [selectedFy, setSelectedFy] = useState();
  const [summaryOnly, setSummaryOnly] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("pdf");
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);
  const [submittedOpen, setSubmittedOpen] = useState(false);

  const handleApiError = (e, fallback) => {
    if (handleDistributorAuthFailure(e.response, e.response?.data)) return "";
    return e.response?.data?.message || e.response?.data?.error || fallback;
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/capital_gain_clients.php`, authConfig());
        setClients(r.data?.clients || []);
      } catch (e) {
        setClientError(handleApiError(e, "Unable to load clients."));
      } finally {
        setClientsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedClient = useMemo(
    () => clients.find((c) => c.pan === selectedPan) || null,
    [clients, selectedPan]
  );

  const onClientChange = async (pan) => {
    setSelectedPan(pan);
    setSelectedFy(undefined);
    setYears([]);
    setYearError("");
    setSummary(null);
    setDetails(null);
    setReportError("");
    const client = clients.find((c) => c.pan === pan);
    if (!client) return;
    setYearsLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/capital_gain_financial_years.php`, {
        ...authConfig(),
        params: { pan },
      });
      setYears(r.data?.financial_years || []);
      if (!(r.data?.financial_years || []).length) setYearError("No transactions found for this client.");
    } catch (e) {
      setYearError(handleApiError(e, "Unable to fetch financial years."));
    } finally {
      setYearsLoading(false);
    }
  };

  const loadDetails = async () => {
    if (!selectedPan || !selectedFy) return;
    try {
      const r = await axios.get(`${API_BASE}/get_capital_gains_details.php`, {
        ...authConfig(),
        params: { pan: selectedPan, financial_year: selectedFy },
      });
      setDetails(r.data || null);
    } catch (e) {
      setReportError(handleApiError(e, "Unable to load capital gains details."));
    }
  };

  const onSummaryOnlyChange = async (e) => {
    const checked = e.target.checked;
    setSummaryOnly(checked);
    // Once summary is on screen, unchecking should reveal details immediately.
    if (!checked && summary && !details && selectedPan && selectedFy) {
      setReportLoading(true);
      setReportError("");
      await loadDetails();
      setReportLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedPan || !selectedFy) return;
    setReportLoading(true);
    setReportError("");
    setSummary(null);
    setDetails(null);

    try {
      const summaryRequest = axios.get(`${API_BASE}/get_capital_gains_summary.php`, {
        ...authConfig(),
        params: { pan: selectedPan, financial_year: selectedFy },
      });
      const detailsRequest = summaryOnly
        ? Promise.resolve(null)
        : axios.get(`${API_BASE}/get_capital_gains_details.php`, {
            ...authConfig(),
            params: { pan: selectedPan, financial_year: selectedFy },
          });

      const [summaryResponse, detailsResponse] = await Promise.all([summaryRequest, detailsRequest]);
      setSummary(summaryResponse?.data || null);
      setDetails(detailsResponse?.data || null);
    } catch (e) {
      setReportError(handleApiError(e, "Unable to generate capital gains report."));
    } finally {
      setReportLoading(false);
    }
  };

  const queueDownload = async () => {
    if (!selectedPan || !selectedFy) return;
    setDownloadSubmitting(true);
    try {
      await axios.post(`${API_BASE}/capital_gain_report_job_start.php`, {
        pan: selectedPan,
        financial_year: selectedFy,
        output_format: downloadFormat,
        include_details: !summaryOnly,
      }, authConfig());
      setDownloadOpen(false);
      setSubmittedOpen(true);
    } catch (e) {
      const msg=handleApiError(e, "Unable to submit report request.");
      if(msg) Modal.error({ title: "Report Request Failed", content: msg });
    } finally {
      setDownloadSubmitting(false);
    }
  };

  const canGenerate = selectedClient && selectedFy && !yearsLoading && !reportLoading;

  return (
    <div style={{ padding: 10 }}>
      <Card title="Capital Gains Report" bodyStyle={{ padding: "14px 18px" }}>
        {clientError && <Alert style={{ marginBottom: 10 }} type="error" showIcon message={clientError} />}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 1.6fr) minmax(220px, 1fr) auto", gap: 14, alignItems: "end" }}>
          <div>
            <Typography.Text strong>Select Client</Typography.Text>
            <Select
              showSearch
              allowClear
              loading={clientsLoading}
              disabled={clientsLoading}
              placeholder="Select client name (PAN)"
              value={selectedPan}
              onChange={onClientChange}
              optionFilterProp="label"
              style={{ width: "100%", marginTop: 5 }}
              options={clients.map((c) => ({ value: c.pan, label: `${c.invester_name} (${c.pan})` }))}
            />
          </div>

          <div>
            <Typography.Text strong>Select Financial Year</Typography.Text>
            <Select
              placeholder={selectedClient ? "Select financial year" : "Select client first"}
              disabled={!selectedClient || yearsLoading || !years.length}
              loading={yearsLoading}
              value={selectedFy}
              onChange={(v) => { setSelectedFy(v); setSummary(null); setDetails(null); setReportError(""); }}
              style={{ width: "100%", marginTop: 5 }}
              options={years.map((y) => ({ value: y.label, label: y.label }))}
            />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              disabled={!canGenerate}
              loading={reportLoading}
              onClick={generateReport}
              style={{ width: 135 }}
            >
              Generate
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={!selectedClient || !selectedFy || yearsLoading}
              onClick={() => setDownloadOpen(true)}
              style={{ width: 135 }}
            >
              Download
            </Button>
          </div>
        </div>

        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 14 }}>
          <Checkbox checked={summaryOnly} onChange={onSummaryOnlyChange}>Summary Only</Checkbox>
          {yearsLoading && selectedClient && <><Spin size="small" /><Typography.Text type="secondary">Fetching financial years...</Typography.Text></>}
        </div>
        {yearError && <Alert style={{ marginTop: 8 }} type="warning" showIcon message={yearError} />}
      </Card>

      {reportError && <Alert style={{ marginTop: 16 }} type="error" showIcon message={reportError} />}

      {summary && (
        <Card style={{ marginTop: 8 }} bodyStyle={{ padding: "12px 16px 16px" }}>
          <div style={{ textAlign: "center", marginBottom: 8 }}>
            <Typography.Title level={4} style={{ margin: "0 0 2px" }}>Capital Gains Report</Typography.Title>
            <Typography.Text strong>{summary.client_name || selectedClient?.invester_name}</Typography.Text>
            <div>
              <Typography.Text>PAN: {summary.pan} &nbsp; | &nbsp; Financial Year: {summary.financial_year}</Typography.Text>
            </div>
          </div>

          <Typography.Title level={5} style={{ margin: "8px 0" }}>Summary</Typography.Title>
          <SummaryTable matrix={summary.summary_matrix} />

          {!summaryOnly && details && (
            <>
              <Typography.Title level={5} style={{ marginTop: 26 }}>Transaction Details</Typography.Title>
              <DetailsTable rows={details.transaction_details} />
            </>
          )}
        </Card>
      )}

      <Modal
        title="Download Capital Gains Report"
        open={downloadOpen}
        onCancel={() => !downloadSubmitting && setDownloadOpen(false)}
        okText="Submit Report"
        cancelText="Cancel"
        confirmLoading={downloadSubmitting}
        onOk={queueDownload}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Select the file format. The downloaded report will always contain Overall Summary, Scheme/Folio-wise Summary and Transaction Details.
        </Typography.Paragraph>
        <Radio.Group value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
          <Radio value="pdf">PDF</Radio>
          <Radio value="excel">Excel</Radio>
        </Radio.Group>
      </Modal>

      <Modal
        title="Report Request Submitted"
        open={submittedOpen}
        onCancel={() => setSubmittedOpen(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setSubmittedOpen(false)}>OK</Button>,
        ]}
        width={665}
      >
        <Alert
          type="success"
          showIcon
          message="Report Request Submitted"
          description={
            <div style={{ fontSize: 16, lineHeight: 1.7 }}>
              <p>Your report request has been submitted successfully and is being prepared in the background.</p>
              <p><strong>Report कहाँ मिलेगी:</strong> Dashboard → Reports → Downloads</p>
              <p>Downloads page पर <strong>Refresh</strong> दबाएँ। Report तैयार होने पर status <strong>Ready</strong> और <strong>Download</strong> button दिखाई देगा.</p>
              <p style={{ marginBottom: 0 }}>तैयार होने के बाद report <strong>24 hours</strong> तक download के लिए उपलब्ध रहेगी.</p>
            </div>
          }
        />
      </Modal>
    </div>
  );
}
